import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { BrowserCodeReader, BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import {
  Camera,
  ImageOff,
  ImagePlus,
  Minus,
  PenLine,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
  X,
} from "lucide-react";
import { api } from "../../convex/_generated/api";
import { formatNumber } from "../lib/format";
import { useLoggingFlow } from "../lib/logging-flow";
import type { LoggingFlowKind, LoggingFlowPayload } from "../lib/logging-flow";
import { lookupOffBarcode, OFF_ATTRIBUTION, offProductImageUrl } from "../lib/openfoodfacts-client";
import { usePlate } from "../lib/plate";
import { startScannerSession } from "./barcode-scanner-session";
import {
  availableServingModes,
  clampQuantity,
  classifyCameraError,
  classifyLookupError,
  customFoodArgsFromProduct,
  defaultServingMode,
  isValidBarcodeInput,
  normalizeBarcodeInput,
  plateItemFromProduct,
  productDisplayName,
  productNutrition,
  quantityLabel,
  reviewItemFromProduct,
  servingModeLabel,
  showIncompleteBadge,
  stepQuantity,
} from "./barcode-utils";
import type {
  CameraErrorKind,
  LookupErrorKind,
  OffProduct,
  ServingMode,
} from "./barcode-utils";

// Barcode scanner flow (plan todo 16): live camera decode (ZXing
// BrowserMultiFormatReader, EAN/UPC hints only) -> browser-direct
// OpenFoodFacts lookup (per-user rate limit; preflight-free simple requests)
// -> product card with serving/quantity selection -> Add to plate / Add to
// meal (review) / Save as frequent food. Not-found offers the four spec
// fallbacks; camera failures degrade to manual digit entry + photo decode.
// Missing nutriments stay undefined end-to-end; the camera stream is always
// released on unmount/close via the session cleanup contract. Demo mode
// resolves any valid barcode to a local demo product (no network call).

export type ScannerPhase =
  | { readonly kind: "scanning" }
  | { readonly kind: "looking-up"; readonly barcode: string }
  | { readonly kind: "found"; readonly barcode: string; readonly product: OffProduct }
  | { readonly kind: "not-found"; readonly barcode: string }
  | { readonly kind: "lookup-error"; readonly barcode: string; readonly errorKind: LookupErrorKind }
  | { readonly kind: "manual"; readonly cameraError: CameraErrorKind | null };

function createBarcodeReader(): BrowserMultiFormatReader {
  const hints = new Map<DecodeHintType, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
  ]);
  return new BrowserMultiFormatReader(hints);
}

// Demo-mode lookup: a realistic local product so the showcase never touches
// the network. Any syntactically valid barcode resolves to it after a short
// simulated delay; the image reuses the bundled demo asset.
const DEMO_LOOKUP_DELAY_MS = 500;

function demoProductForBarcode(barcode: string): OffProduct {
  return {
    barcode,
    name: "Demo Honey Nut Granola Bar",
    brands: "BiteTrack Demo",
    servingSize: "40 g",
    imageUrl: "/demo-food.jpg",
    per100g: { calories: 440, proteinG: 7.5, carbsG: 65, fatG: 17.5, fiberG: 5 },
    perServing: { calories: 176, proteinG: 3, carbsG: 26, fatG: 7, fiberG: 2 },
    incomplete: false,
    noNutritionData: false,
    hasBarcode: true,
  };
}

// Lookup controller (the repo has no jsdom, so this is the unit-testable
// seam for the decode -> lookup -> phase path): resolves a validated barcode
// to the next scanner phase. Demo mode returns the local demo product after
// a simulated delay — no network call. Phase-transition logs carry the
// [Frontend][BarcodeScanner] prefix so production logs show where a lookup
// stops.
export async function resolveBarcodeLookup(
  barcode: string,
  demoMode: boolean,
): Promise<ScannerPhase> {
  if (demoMode) {
    if (!isValidBarcodeInput(barcode)) {
      console.warn("[Frontend][BarcodeScanner] Demo lookup rejected by validation", {
        digits: barcode.length,
      });
      return { kind: "lookup-error", barcode, errorKind: "validation" };
    }
    console.log("[Frontend][BarcodeScanner] Demo lookup started (no network call)", { barcode });
    await new Promise((resolve) => setTimeout(resolve, DEMO_LOOKUP_DELAY_MS));
    console.log("[Frontend][BarcodeScanner] Demo product returned", { barcode });
    return { kind: "found", barcode, product: demoProductForBarcode(barcode) };
  }
  try {
    const result = await lookupOffBarcode(barcode);
    if (result.found) {
      console.log("[Frontend][BarcodeScanner] Product found", { barcode });
      return { kind: "found", barcode, product: result.product };
    }
    console.log("[Frontend][BarcodeScanner] Product not in OFF database", { barcode });
    return { kind: "not-found", barcode };
  } catch (error: unknown) {
    const errorKind = classifyLookupError(error);
    console.error("[Frontend][BarcodeScanner] Product lookup failed", {
      barcode,
      errorKind,
      error: error instanceof Error ? error.message : String(error),
    });
    return { kind: "lookup-error", barcode, errorKind };
  }
}

function MetricCell({ label, value, unit }: {
  readonly label: string;
  readonly value: number | undefined;
  readonly unit: string;
}) {
  return (
    <div className="flex flex-col items-center rounded-lg bg-muted px-1 py-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text">
        {value === undefined ? "—" : `${formatNumber(value)}${unit}`}
      </span>
    </div>
  );
}

// Found-product card: image/name/brand, incomplete badge, serving selector,
// quantity stepper, per-selection nutrition, and the three actions.
export interface BarcodeProductCardProps {
  readonly barcode: string;
  readonly product: OffProduct;
  readonly onScanAnother: () => void;
  // OFF attribution (CC BY-SA) shows on real lookups; demo mode hides it
  // because the local demo product is not OFF data.
  readonly showAttribution?: boolean;
  // Test hooks (renderToStaticMarkup cannot click): preselect the serving
  // mode/quantity so suites can assert both branches of the scaling logic.
  readonly initialMode?: ServingMode;
  readonly initialQuantity?: number;
}

export function BarcodeProductCard({
  barcode,
  product,
  onScanAnother,
  showAttribution = true,
  initialMode,
  initialQuantity,
}: BarcodeProductCardProps) {
  const [mode, setMode] = useState<ServingMode>(() => initialMode ?? defaultServingMode(product));
  const [quantity, setQuantity] = useState(() => clampQuantity(initialQuantity ?? 1));
  const [addedToPlate, setAddedToPlate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const { addItem } = usePlate();
  const { startFlow, closeFlow } = useLoggingFlow();
  const createCustomFood = useMutation(api.customFoods.create);

  const modes = availableServingModes(product);
  const nutrition = productNutrition(product, mode, quantity);
  const name = productDisplayName(product);
  const imageUrl = offProductImageUrl(product);
  const showCover = imageUrl !== undefined && !imageFailed;

  const addToPlate = () => {
    addItem(plateItemFromProduct(product, barcode, mode, quantity));
    setAddedToPlate(true);
  };

  const addToMeal = () => {
    console.log("[Frontend][BarcodeScanner] Routing product to meal review", {
      barcode,
      mode,
      quantity,
    });
    startFlow("review", {
      items: [reviewItemFromProduct(product, mode, quantity)],
      source: "barcode",
    });
  };

  const saveCustomFood = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await createCustomFood(customFoodArgsFromProduct(product, barcode));
      console.log("[Frontend][BarcodeScanner] Product saved as frequent food", { barcode });
      setSaved(true);
    } catch (error: unknown) {
      console.error("[Frontend][BarcodeScanner] Saving frequent food failed", {
        barcode,
        error: error instanceof Error ? error.message : String(error),
      });
      setSaveError("Could not save this food. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="barcode-product-card" className="rounded-2xl border border-border bg-surface p-4">
      {showCover ? (
        <div
          data-testid="barcode-product-image-banner"
          className="flex h-44 w-full items-center justify-center rounded-xl bg-muted p-2"
        >
          <img
            src={imageUrl}
            alt={name}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </div>
      ) : (
        <div
          data-testid="barcode-product-image-fallback"
          className="flex h-20 w-full items-center justify-center gap-2 rounded-xl bg-muted text-text-muted"
        >
          <ImageOff size={18} aria-hidden="true" />
          <span className="text-xs">No product photo</span>
        </div>
      )}
      <div className="mt-3">
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Barcode product
        </p>
        <h3 className="line-clamp-2 text-base font-semibold text-text" data-testid="barcode-product-name">
          {name}
        </h3>
        {product.brands !== undefined && (
          <p className="line-clamp-1 text-sm text-text-muted" data-testid="barcode-product-brand">
            {product.brands}
          </p>
        )}
        {showIncompleteBadge(product) && (
          <span
            data-testid="barcode-incomplete-badge"
            className="mt-1 inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning"
          >
            Incomplete nutrition
          </span>
        )}
      </div>

      <div className="mt-4 flex gap-2" role="group" aria-label="Serving basis">
        {modes.map((option) => (
          <button
            key={option}
            type="button"
            data-testid={`barcode-mode-${option}`}
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
            className={`min-h-[44px] flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-all duration-200 ease-out active:scale-[0.98] select-none ${
              mode === option
                ? "border-accent bg-accent-light text-accent-dark"
                : "border-border bg-surface text-text-muted active:bg-muted"
            }`}
          >
            {servingModeLabel(product, option)}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between rounded-xl border border-border bg-muted px-2 py-1.5">
        <button
          type="button"
          data-testid="barcode-quantity-minus"
          aria-label="Decrease quantity"
          onClick={() => setQuantity((current) => stepQuantity(current, -1))}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text active:bg-surface active:scale-[0.98] select-none"
        >
          <Minus size={18} strokeWidth={2} />
        </button>
        <span className="text-base font-semibold text-text" data-testid="barcode-quantity-label">
          {quantityLabel(mode, quantity)}
        </span>
        <button
          type="button"
          data-testid="barcode-quantity-plus"
          aria-label="Increase quantity"
          onClick={() => setQuantity((current) => stepQuantity(current, 1))}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text active:bg-surface active:scale-[0.98] select-none"
        >
          <Plus size={18} strokeWidth={2} />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-5 gap-1.5" data-testid="barcode-metrics">
        <MetricCell label="kcal" value={nutrition.calories} unit="" />
        <MetricCell label="Protein" value={nutrition.proteinG} unit="g" />
        <MetricCell label="Carbs" value={nutrition.carbsG} unit="g" />
        <MetricCell label="Fat" value={nutrition.fatG} unit="g" />
        <MetricCell label="Fibre" value={nutrition.fiberG} unit="g" />
      </div>

      {addedToPlate ? (
        <div className="mt-4" data-testid="barcode-added-to-plate">
          <p className="rounded-xl bg-accent-light px-3 py-2 text-center text-sm font-medium text-accent-dark">
            Added to your plate
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid="barcode-scan-another"
              onClick={onScanAnother}
              className="min-h-[44px] flex-1 rounded-xl border border-border bg-surface px-3 py-2 text-sm font-medium text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
            >
              Scan another
            </button>
            <button
              type="button"
              data-testid="barcode-plate-done"
              onClick={closeFlow}
              className="min-h-[44px] flex-1 rounded-xl bg-accent px-3 py-2 text-sm font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="barcode-add-to-plate"
              onClick={addToPlate}
              className="min-h-[44px] flex-1 rounded-xl border border-accent bg-surface px-3 py-2 text-sm font-medium text-accent-dark transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
            >
              Add to plate
            </button>
            <button
              type="button"
              data-testid="barcode-add-to-meal"
              onClick={addToMeal}
              className="min-h-[44px] flex-1 rounded-xl bg-accent px-3 py-2 text-sm font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
            >
              Add to meal
            </button>
          </div>
          <button
            type="button"
            data-testid="barcode-save-frequent"
            onClick={() => void saveCustomFood()}
            disabled={saving || saved}
            className="min-h-[44px] w-full rounded-xl px-3 py-2 text-sm font-medium text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none disabled:opacity-50"
          >
            {saved ? "Saved to your frequent foods" : saving ? "Saving…" : "Save as frequent food"}
          </button>
          {saveError !== null && (
            <p className="text-center text-sm text-danger" role="alert" data-testid="barcode-save-error">
              {saveError}
            </p>
          )}
        </div>
      )}
      {showAttribution && (
        <p data-testid="barcode-off-attribution" className="mt-3 text-center text-[11px] text-text-muted">
          {OFF_ATTRIBUTION}
        </p>
      )}
    </div>
  );
}

// Not-found sheet: the four spec fallbacks, each routing to a real flow.
export interface BarcodeNotFoundProps {
  readonly barcode: string;
  readonly onScanAnother: () => void;
}

const NOT_FOUND_FALLBACKS: readonly {
  readonly label: string;
  readonly kind: LoggingFlowKind;
  readonly payload?: LoggingFlowPayload;
  readonly testId: string;
}[] = [
  { label: "Scan nutrition label", kind: "label", testId: "barcode-fallback-label" },
  { label: "Search by name", kind: "search", testId: "barcode-fallback-search" },
  { label: "Enter manually", kind: "quickAdd", testId: "barcode-fallback-manual" },
  { label: "Photograph the product", kind: "photo", testId: "barcode-fallback-photo" },
];

export function BarcodeNotFound({ barcode, onScanAnother }: BarcodeNotFoundProps) {
  const { startFlow } = useLoggingFlow();
  return (
    <div data-testid="barcode-not-found" className="rounded-2xl border border-border bg-surface p-4">
      <h3 className="text-base font-semibold text-text">Product not found</h3>
      <p className="mt-1 text-sm text-text-muted">
        Barcode {barcode} is not in the OpenFoodFacts database. You can still log this product:
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {NOT_FOUND_FALLBACKS.map((fallback) => (
          <button
            key={fallback.kind}
            type="button"
            data-testid={fallback.testId}
            onClick={() => {
              console.log("[Frontend][BarcodeScanner] Not-found fallback chosen", {
                barcode,
                kind: fallback.kind,
              });
              startFlow(fallback.kind, {
                ...(fallback.kind === "search" ? { query: barcode } : {}),
                ...fallback.payload,
              });
            }}
            className="flex min-h-[44px] items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left text-sm font-medium text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
          >
            {fallback.kind === "label" && <ScanLine size={18} className="text-accent" aria-hidden="true" />}
            {fallback.kind === "search" && <Search size={18} className="text-accent" aria-hidden="true" />}
            {fallback.kind === "quickAdd" && <PenLine size={18} className="text-accent" aria-hidden="true" />}
            {fallback.kind === "photo" && <Camera size={18} className="text-accent" aria-hidden="true" />}
            {fallback.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="barcode-not-found-rescan"
          onClick={onScanAnother}
          className="min-h-[44px] rounded-xl px-3 py-2 text-sm font-medium text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
        >
          Scan another barcode
        </button>
      </div>
    </div>
  );
}

// Manual entry fallback (camera denied/unavailable, or by choice): digit
// input with 8-14 validation + "scan from photo" via decodeFromImageElement.
export interface BarcodeManualEntryProps {
  readonly cameraError: CameraErrorKind | null;
  readonly notice: string | null;
  readonly photoBusy: boolean;
  readonly photoError: string | null;
  readonly onSubmit: (barcode: string) => void;
  readonly onPhotoFile: (file: File) => void;
  readonly onRetryCamera: () => void;
}

function cameraGuidance(kind: CameraErrorKind): string {
  if (kind === "denied") {
    return "Camera access is blocked. Allow camera permission for this app in your device or browser settings, then tap Try camera again.";
  }
  if (kind === "unavailable") {
    return "No usable camera was found on this device. Type the barcode number below or scan it from a photo.";
  }
  return "The camera could not be started. Type the barcode number below or scan it from a photo.";
}

export function BarcodeManualEntry({
  cameraError,
  notice,
  photoBusy,
  photoError,
  onSubmit,
  onPhotoFile,
  onRetryCamera,
}: BarcodeManualEntryProps) {
  const [input, setInput] = useState("");
  const [touched, setTouched] = useState(false);
  const valid = isValidBarcodeInput(input);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div data-testid="barcode-manual" className="rounded-2xl border border-border bg-surface p-4">
      {cameraError !== null && (
        <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-text" data-testid="barcode-camera-guidance">
          {cameraGuidance(cameraError)}
        </p>
      )}
      {notice !== null && (
        <p className="mt-2 rounded-xl bg-muted px-3 py-2 text-sm text-text-muted" data-testid="barcode-manual-notice">
          {notice}
        </p>
      )}
      <label className="mt-3 block">
        <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
          Barcode number
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          data-testid="barcode-manual-input"
          value={input}
          onChange={(event) => setInput(event.target.value.replace(/[^\d]/g, ""))}
          onBlur={() => setTouched(true)}
          placeholder="e.g. 3017620422003"
          className="mt-1 w-full rounded-xl border border-border bg-surface px-4 py-3 text-base text-text focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      {touched && !valid && input.length > 0 && (
        <p className="mt-1 text-sm text-danger" role="alert" data-testid="barcode-manual-error">
          Enter 8 to 14 digits.
        </p>
      )}
      <button
        type="button"
        data-testid="barcode-manual-submit"
        disabled={!valid}
        onClick={() => onSubmit(normalizeBarcodeInput(input))}
        className="mt-3 min-h-[44px] w-full rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none disabled:opacity-50"
      >
        Look up product
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        data-testid="barcode-photo-input"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) onPhotoFile(file);
          event.target.value = "";
        }}
      />
      <button
        type="button"
        data-testid="barcode-photo-fallback"
        disabled={photoBusy}
        onClick={() => fileInputRef.current?.click()}
        className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-sm font-medium text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none disabled:opacity-50"
      >
        <ImagePlus size={18} className="text-accent" aria-hidden="true" />
        {photoBusy ? "Scanning photo…" : "Scan barcode from a photo"}
      </button>
      {photoError !== null && (
        <p className="mt-1 text-sm text-danger" role="alert" data-testid="barcode-photo-error">
          {photoError}
        </p>
      )}
      {cameraError !== null && (
        <button
          type="button"
          data-testid="barcode-camera-retry"
          onClick={onRetryCamera}
          className="mt-2 min-h-[44px] w-full rounded-xl px-3 py-2 text-sm font-medium text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
        >
          Try camera again
        </button>
      )}
    </div>
  );
}

export interface BarcodeScannerProps {
  readonly onClose: () => void;
  // Demo mode: lookups resolve to a local demo product (no network call).
  readonly demoMode?: boolean;
  // Test hook: render a specific phase under renderToStaticMarkup (no DOM
  // effects run in SSR, so the camera never starts in unit tests).
  readonly initialPhase?: ScannerPhase;
}

export default function BarcodeScanner({ onClose, demoMode = false, initialPhase }: BarcodeScannerProps) {
  const [phase, setPhase] = useState<ScannerPhase>(initialPhase ?? { kind: "scanning" });
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [manualNotice, setManualNotice] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const runLookup = useCallback(async (barcode: string) => {
    setPhase({ kind: "looking-up", barcode });
    console.log("[Frontend][BarcodeScanner] Lookup started", { barcode, demoMode });
    setPhase(await resolveBarcodeLookup(barcode, demoMode));
  }, [demoMode]);

  const handleDecodedText = useCallback(
    (text: string) => {
      const barcode = normalizeBarcodeInput(text);
      if (!isValidBarcodeInput(barcode)) {
        console.warn("[Frontend][BarcodeScanner] Decoded text is not a valid product barcode", {
          digits: barcode.length,
        });
        setManualNotice("That code is not a supported product barcode. Type the 8 to 14 digit number below.");
        setPhase({ kind: "manual", cameraError: null });
        return;
      }
      console.log("[Frontend][BarcodeScanner] Barcode decoded", { barcode });
      void runLookup(barcode);
    },
    [runLookup],
  );

  // Live camera session. Cleanup ALWAYS stops the decode loop and releases
  // the video stream (cancelled-flag race guard inside the session) — no
  // camera stays alive after unmount, close, or a phase transition.
  useEffect(() => {
    if (phase.kind !== "scanning") return;
    const video = videoRef.current;
    if (video === null) return;
    console.log("[Frontend][BarcodeScanner] Scanner session starting", { demoMode });
    const session = startScannerSession({
      reader: createBarcodeReader(),
      video,
      cleanVideo: (element) => {
        BrowserCodeReader.cleanVideoSource(element);
      },
      onDecode: handleDecodedText,
      onFailure: (error) => {
        const cameraError = classifyCameraError(error);
        console.error("[Frontend][BarcodeScanner] Camera session failed", {
          cameraError,
          error: error instanceof Error ? error.message : String(error),
        });
        setPhase({ kind: "manual", cameraError });
      },
    });
    return () => {
      session.stop();
    };
  }, [phase.kind, handleDecodedText, demoMode]);

  const resetToScanning = () => {
    setPhotoError(null);
    setManualNotice(null);
    setPhase({ kind: "scanning" });
  };

  const handlePhotoFile = (file: File) => {
    setPhotoBusy(true);
    setPhotoError(null);
    const url = URL.createObjectURL(file);
    void (async () => {
      try {
        const image = new Image();
        image.src = url;
        await image.decode();
        const result = await createBarcodeReader().decodeFromImageElement(image);
        console.log("[Frontend][BarcodeScanner] Barcode decoded from photo");
        handleDecodedText(result.getText());
      } catch (error: unknown) {
        console.warn("[Frontend][BarcodeScanner] No barcode found in photo", {
          error: error instanceof Error ? error.name : String(error),
        });
        setPhotoError("We couldn't find a barcode in that photo. Try a sharper, closer shot of the barcode.");
      } finally {
        URL.revokeObjectURL(url);
        setPhotoBusy(false);
      }
    })();
  };

  return (
    <div data-testid="barcode-scanner" className="fixed inset-0 z-40 flex flex-col bg-muted">
      <header className="w-full shrink-0 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-xl items-center justify-between px-4 py-3">
          <h2 className="text-lg font-semibold text-text">Scan barcode</h2>
          <button
            type="button"
            data-testid="barcode-close"
            aria-label="Close barcode scanner"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-8">
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-4">
          {phase.kind === "scanning" && (
            <>
              <div className="relative overflow-hidden rounded-2xl bg-black">
                <video
                  ref={videoRef}
                  data-testid="barcode-video"
                  className="aspect-[4/3] w-full object-cover"
                  muted
                  playsInline
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-xl border-2 border-accent"
                />
              </div>
              <div className="rounded-2xl border border-border bg-surface p-4">
                <p className="flex items-center gap-2 text-sm font-medium text-text">
                  <ScanLine size={18} className="text-accent" aria-hidden="true" />
                  Point the camera at the product barcode
                </p>
                <p className="mt-1 text-sm text-text-muted">
                  Hold steady and make sure the barcode is well lit. EAN and UPC barcodes are supported.
                </p>
                <button
                  type="button"
                  data-testid="barcode-enter-manual"
                  onClick={() => setPhase({ kind: "manual", cameraError: null })}
                  className="mt-2 min-h-[44px] w-full rounded-xl px-3 py-2 text-sm font-medium text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
                >
                  Enter the code manually
                </button>
              </div>
            </>
          )}

          {phase.kind === "looking-up" && (
            <div data-testid="barcode-looking-up" className="rounded-2xl border border-border bg-surface p-4">
              <div className="relative overflow-hidden rounded-xl bg-muted">
                <div className="h-24 animate-pulse" />
              </div>
              <p className="mt-3 text-center text-sm text-text-muted">
                Looking up product {phase.barcode}…
              </p>
            </div>
          )}

          {phase.kind === "found" && (
            <BarcodeProductCard
              barcode={phase.barcode}
              product={phase.product}
              onScanAnother={resetToScanning}
              showAttribution={!demoMode}
            />
          )}

          {phase.kind === "not-found" && (
            <BarcodeNotFound barcode={phase.barcode} onScanAnother={resetToScanning} />
          )}

          {phase.kind === "lookup-error" && (
            <div data-testid="barcode-lookup-error" className="rounded-2xl border border-border bg-surface p-4">
              <h3 className="text-base font-semibold text-text">Lookup failed</h3>
              <p className="mt-1 text-sm text-text-muted">
                {phase.errorKind === "unavailable"
                  ? "OpenFoodFacts is temporarily unavailable. Check your connection and try again."
                  : "The product lookup did not work. Try again or enter the barcode manually."}
              </p>
              <button
                type="button"
                data-testid="barcode-lookup-retry"
                onClick={() => void runLookup(phase.barcode)}
                className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none"
              >
                <RefreshCw size={18} aria-hidden="true" />
                Retry lookup
              </button>
              <button
                type="button"
                data-testid="barcode-lookup-manual"
                onClick={() => setPhase({ kind: "manual", cameraError: null })}
                className="mt-2 min-h-[44px] w-full rounded-xl px-3 py-2 text-sm font-medium text-accent transition-all duration-200 ease-out active:bg-accent-light active:scale-[0.98] select-none"
              >
                Enter barcode manually
              </button>
            </div>
          )}

          {phase.kind === "manual" && (
            <BarcodeManualEntry
              cameraError={phase.cameraError}
              notice={manualNotice}
              photoBusy={photoBusy}
              photoError={photoError}
              onSubmit={(barcode) => {
                console.log("[Frontend][BarcodeScanner] Manual barcode submitted", {
                  digits: barcode.length,
                });
                void runLookup(barcode);
              }}
              onPhotoFile={handlePhotoFile}
              onRetryCamera={resetToScanning}
            />
          )}
        </div>
      </main>
    </div>
  );
}
